import React, { useState, useEffect, useCallback } from "react";
import {
  Input,
  Button,
  Table,
  Modal,
  Form,
  Checkbox,
  Row,
  Col,
  Typography,
  Card,
  Space,
  Spin,
  Switch,
} from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import DeleteConfirmation from "../../components/DeleteConfirmation.tsx";
import topicService from "../../services/topic-service.ts";
import questionService from "../../services/question-service.ts";
import Constant from "../../global/Constant.ts";
import { useDispatch } from "react-redux";
import { setLoading, addNotification } from "../../redux/globalSlice.ts";

const { Text } = Typography;

const AssessmentForm = () => {
  const [visible, setVisible] = useState(false);
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
  const [TopicId, setTopicId] = useState(0);
  const [questionPool, setQuestionPool] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [loading, setBtnLoad] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalTopics, setTotalTopics] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [questionPoolLoading, setQuestionPoolLoading] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [newStatus, setNewStatus] = useState(false);
  const dispatch = useDispatch();

  const [form] = Form.useForm();

  const columns = [
    {
      title: "Sl.No",
      key: "slNo",
      width: 80,
      align: "left",
      className: "vm__no-wrap",
      render: (_, record, index) => {
        return (currentPage - 1) * perPage + index + 1;
      },
    },
    {
      title: "Topic",
      dataIndex: "TopicName",
      key: "TopicName",
      width: 250,
      className: "vm__no-wrap",
    },
    {
      title: "Minimum Participant Count (%)",
      dataIndex: "MinimumParticipationCount",
      key: "MinimumParticipationCount",
      width: 250,
      align: "center",
      className: "vm__no-wrap",
    },
    {
      title: "Minimum Succeed Count (%)",
      dataIndex: "MinimumSucceedCount",
      key: "MinimumSucceedCount",
      width: 250,
      align: "center",
      className: "vm__no-wrap",
    },
    {
      title: "No. of Questions",
      dataIndex: "questionCount",
      key: "questionCount",
      width: 200,
      align: "center",
      className: "vm__no-wrap",
    },
    {
      title: "Status",
      dataIndex: "Status",
      key: "Status",
      width: 100,
      className: "vm__no-wrap",
      render: (_, record) => (
        <>
          <div className="vm__d-inline-block vm__valign-sup">
            <Switch
              size="small"
              checked={record.Status === 1}
              onChange={(checked) => handleStatusChange(record, checked)}
            />
          </div>
        </>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      align: "center",
      className: "vm__no-wrap",
      render: (_, record) => (
        <Space>
          <span className="vm__table-icons" onClick={() => handleEdit(record)}>
            <EditOutlined />
          </span>
        </Space>
      ),
    },
  ];

  const validateCount = (_, value) =>
    value >= 0 && value <= 100
      ? Promise.resolve()
      : Promise.reject("Count must be between 0 and 100");

  const getAllTopics = useCallback(
    async (pageIndex = 1, perSize = 10) => {
      try {
        dispatch(setLoading(true));
        const response = await topicService.getAllTopics(Constant.topic, {
          pageIndex,
          perSize,
        });
        setAssessments(response.topics);
        setTotalTopics(response.totalTopics);
        dispatch(setLoading(false));
      } catch (error) {
        dispatch(setLoading(false));
        console.error("Error fetching topics:", error);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    getAllTopics(currentPage, perPage);
  }, [currentPage, perPage, getAllTopics]);

  const handleAddTopic = async () => {
    dispatch(setLoading(true));
    setTopicId(0);
    setSearchTerm("");
    form.resetFields();
    try {
      const response = await questionService.getAllEnabledQuestions(
        Constant.question
      );
      if (response.length === 0) {
        dispatch(
          addNotification({
            title: "Warning",
            description:
              "No Questions found, Please add Question to create a topic",
            type: "warning",
          })
        );
        return;
      }
      setQuestionPool(response.map((q) => ({ ...q, selected: false })));
      setVisible(true);
    } catch (error) {
      console.error("Error fetching all questions:", error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleEdit = async (record) => {
    setTopicId(record.TopicsID);
    dispatch(setLoading(true));

    form.setFieldsValue({
      TopicName: record.TopicName,
      MinimumParticipationCount: record.MinimumParticipationCount,
      MinimumSucceedCount: record.MinimumSucceedCount,
    });

    await new Promise((resolve) => {
      getQuestionPool(record.TopicsID);
      resolve(true);
      dispatch(setLoading(false));
      setVisible(true);
    });
  };

  const getQuestionPool = async (topicId) => {
    setQuestionPoolLoading(true);
    try {
      const [allQuestions, selectedQuestions] = await Promise.allSettled([
        questionService.getAllEnabledQuestions(Constant.question),
        topicService.getQuestionByTopicId(Constant.topic, topicId),
      ]);

      let finalQuestions = [];

      if (allQuestions.status === "fulfilled") {
        const questions = allQuestions.value;
        if (selectedQuestions.status === "fulfilled") {
          const selectedIds = new Set(
            selectedQuestions.value.map((q) => q.QuestionID)
          );
          finalQuestions = questions.map((q) => ({
            ...q,
            selected: selectedIds.has(q.QuestionID),
          }));
        } else {
          console.warn(
            "Error fetching selected questions:",
            selectedQuestions.reason
          );
          finalQuestions = questions.map((q) => ({
            ...q,
            selected: false,
          }));
        }
      } else {
        console.warn("Error fetching all questions:", allQuestions.reason);
      }

      setQuestionPool(finalQuestions);
    } catch (error) {
      console.error("Unexpected error fetching questions:", error);
    } finally {
      setQuestionPoolLoading(false);
    }
  };

  const handleSubmitTopic = async () => {
    try {
      setBtnLoad(true);
      const values = await form.validateFields();
      const selectedQuestionIDs = questionPool
        .filter((q) => q.selected)
        .map((q) => q.QuestionID);

      if (selectedQuestionIDs.length === 0) {
        dispatch(
          addNotification({
            title: "Warning",
            description: "At least one question must be selected",
            type: "warning",
          })
        );
        return;
      }

      const data = {
        ...values,
        CreatedBy: 1,
        TopicsID: TopicId,
        QuestionIDs: selectedQuestionIDs,
      };

      if (TopicId === 0) {
        const response = await topicService.createTopic(Constant.topic, data);

        if (response.error) {
          dispatch(
            addNotification({
              title: "Error",
              description: response.error,
              type: "error",
            })
          );
          return;
        }

        dispatch(
          addNotification({
            title: "Success",
            description: "Topic is created",
            type: "success",
          })
        );

        setCurrentPage(1);
        await getAllTopics(1, perPage);
      } else {
        const response = await topicService.updateTopic(Constant.topic, data);

        if (response.error) {
          dispatch(
            addNotification({
              title: "Error",
              description: response.error,
              type: "error",
            })
          );
          return;
        }

        dispatch(
          addNotification({
            title: "Success",
            description: "Topic is updated",
            type: "success",
          })
        );

        await getAllTopics(currentPage, perPage);
      }

      setVisible(false);
    } catch (error) {
      console.error("Failed to submit topic:", error);
      if (error.response && error.response.data && error.response.data.error) {
        dispatch(
          addNotification({
            title: "Error",
            description: error.response.data.error,
            type: "error",
          })
        );
      }
    } finally {
      setBtnLoad(false);
    }
  };

  const handleStatusChange = async (record, checked) => {
    setSelectedTopic(record);
    setNewStatus(checked);
    setDeleteModalVisible(true);
  };

  const handleStatusChangeConfirm = async () => {
    try {
      setConfirmLoading(true);
      const payload = {
        TopicsID: selectedTopic.TopicsID,
        Status: newStatus ? 1 : 0,
        IsUpdate: true,
      };

      const response = await topicService.updateTopic(Constant.topic, payload);

      if (response.error) {
        dispatch(
          addNotification({
            title: "Error",
            description: response.error,
            type: "error",
          })
        );
        return;
      }

      dispatch(
        addNotification({
          title: "Success",
          description: newStatus ? "Topic is enabled" : "Topic is disabled",
          type: "success",
        })
      );

      await getAllTopics(currentPage, perPage);
    } catch (error) {
      console.error("Failed to update topic status:", error);
      dispatch(
        addNotification({
          title: "Error",
          description: "Failed to update topic status",
          type: "error",
        })
      );
    } finally {
      setConfirmLoading(false);
      setDeleteModalVisible(false);
    }
  };

  const handleCancelModal = async () => {
    setVisible(false);
    setQuestionPool([]);
    setSearchTerm("");
  };

  const handleCancelConfirm = () => {
    setConfirmLoading(false);
    setDeleteModalVisible(false);
  };

  const toggleQuestionSelect = (questionId) => {
    setQuestionPool((prev: any) =>
      prev.map((q) =>
        q.QuestionID === questionId ? { ...q, selected: !q.selected } : q
      )
    );
  };

  return (
    <div className="vm__d-flex vm__h-100 vm__flex-column">
      <div className="vm__flex-auto">
        <div className="vm__container">
          <div className="vm__d-flex vm__mb-3 vm__align-items-center">
            <div className="vm__flex-fill">
              <h3 className="vm__page-header">Topics</h3>
            </div>
            <div className="vm__flex-auto">
              <div className="vm__text-end">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddTopic}
                >
                  Create Topic
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="vm__flex-fill vm__overflow-auto">
        <div className="vm__container vm__h-100">
          <div className="vm__custom-table">
            <Table
              dataSource={assessments}
              columns={assessments && assessments.length > 0 ? columns : []}
              rowKey={(record, index) => index}
              className={
                assessments && assessments.length > 0
                  ? "vm__table-container"
                  : ""
              }
              scroll={{ x: true }}
              pagination={{
                current: currentPage,
                pageSize: perPage,
                total: totalTopics,
                onChange: (page, pageSize) => {
                  setCurrentPage(page);
                  setPerPage(pageSize);
                  getAllTopics(page, pageSize);
                },
              }}
            />
          </div>
        </div>
      </div>

      <DeleteConfirmation
        title={"Confirm Status Change"}
        isVisible={isDeleteModalVisible}
        onConfirm={handleStatusChangeConfirm}
        onCancel={handleCancelConfirm}
        confirmLoading={confirmLoading}
        okText={"Yes"}
        content={"Are you sure you want to change the status of this topic?"}
      />

      <Modal
        title={TopicId ? "Edit Topic" : "Add Topic"}
        visible={visible}
        onCancel={() => handleCancelModal()}
        onOk={handleSubmitTopic}
        width={800}
        bodyStyle={{ maxHeight: "calc(100vh - 196px)", minHeight: "100%", height: "100vh" }}
        confirmLoading={loading}
        className="vm__modal"
        maskClosable={false}
        centered
      >
        <Form form={form} layout="vertical" onFinish={handleSubmitTopic} className="vm__h-100">
          <div className="vm__d-flex vm__h-100 vm__flex-column">
            <div className="vm__flex-auto vm__card-bind">
              <Form.Item
                name="TopicName"
                label="Topic"
                className="vm__mb-2"
                rules={[
                  { required: true, message: "" },
                  { whitespace: true, message: "" },
                  { max: 1000, message: "Topic name must be under 1000 characters" },
                ]}
              >
                <Input placeholder="Enter training topic" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="MinimumParticipationCount"
                    label="Minimum Participant Count (%)"
                    className="vm__mb-2"
                    rules={[
                      {
                        required: true,
                        message: "Minimum participant count is required",
                      },
                      { pattern: /^\d+$/, message: "Must be a number" },
                      { validator: validateCount },
                    ]}
                    initialValue={0}
                  >
                    <Input placeholder="Enter count" />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="MinimumSucceedCount"
                    label="Minimum Succeed Count (%)"
                    className="vm__mb-2"
                    rules={[
                      {
                        required: true,
                        message: "Minimum succeed count is required",
                      },
                      { pattern: /^\d+$/, message: "Must be a number" },
                      { validator: validateCount },
                    ]}
                    initialValue={0}
                  >
                    <Input placeholder="Enter count" />
                  </Form.Item>
                </Col>
              </Row>
            </div>
            <div className="vm__flex-fill vm__overflow-auto">
              
                <div
                  className="vm__d-flex vm__flex-column vm__w-100 vm__px-2 vm__h-100">
                  <div className="vm__flex-auto">
                    <div className="vm__d-flex vm__w-100">
                      <Text className="vm__flex-fill" strong>
                        Questions
                      </Text>

                      <Input.Search
                        placeholder="Search questions..."
                        onChange={(e) => setSearchTerm(e.target.value)}
                        value={searchTerm}
                        style={{ marginBottom: 16, width: "50%" }}
                        allowClear
                      />
                    </div>
                  </div>
                  <div className="vm__pe-2 vm__flex-fill vm__overflow-auto">
                  <Spin spinning={questionPoolLoading} className="vm__h-100">
                
                    {questionPool
                      .filter((question) =>
                        question.QuestionText.toLowerCase().includes(
                          searchTerm.toLowerCase()
                        )
                      )
                      .map((question) => (
                        <div key={question.QuestionID} className="vm__mb-2">
                          <Card bodyStyle={{ padding: 12 }}>
                            <Checkbox
                              checked={question.selected}
                              onChange={() =>
                                toggleQuestionSelect(question.QuestionID)
                              }
                            >
                              {question.QuestionText}
                            </Checkbox>
                          </Card>
                        </div>
                      ))}
                  </Spin>
                </div>

                </div>
            </div>
          </div>



        </Form>
      </Modal>
    </div>
  );
};

export default AssessmentForm;
